import { createRef, useEffect, useRef, useState, useId } from "react";
import { createPortal } from "react-dom";
import {
  ActionButton,
  ProgressCircle,
  Text,
  TooltipTrigger,
  Tooltip,
} from "@react-spectrum/s2";
import PlayIcon from "@react-spectrum/s2/icons/Play";
import PauseIcon from "@react-spectrum/s2/icons/Pause";
import RefreshIcon from "@react-spectrum/s2/icons/Refresh";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugins/regions.esm.js";
import { useStore } from "zustand";

import { store } from "@app/store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

// import { styles } from "./css/AudioPlayer.css.js";
// import Refresh from "@spectrum-icons/workflow/Refresh";
// import { PlayIcon, PauseIcon } from "../../assets/index.js";
// import { useAnalytics, AnalyticsSubcategory, AnalyticsEventSubtype } from "../../hooks/useAnalytics.js";
// import { useLocalize } from "../../hooks/useLocalize.js";

/**
 * Formats the given time in seconds into a string representation in the format "mm:ss".
 *
 * @param seconds - The time in seconds to format.
 * @returns The formatted time string in the format "mm:ss".
 */
export function getFormattedTime(seconds) {
  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  // Format the time as 00:00
  const formattedTime = [minutes, secs]
    .map((val) => val.toString().padStart(2, "0"))
    .join(":");

  return formattedTime;
}

/**
 * Effective audio length in seconds, excluding trailing silence.
 *
 * `waveform.getDuration()` returns the full decoded length, which for
 * generated speech typically includes a chunk of trailing silence. This reads
 * the decoded PCM and returns the timestamp just after the last audible sample.
 *
 * @param {import("wavesurfer.js").default|AudioBuffer} source - A WaveSurfer
 *   instance (post-`ready`) or an AudioBuffer.
 * @param {object} [options]
 * @param {number} [options.silenceThreshold=0.01] - Linear amplitude (0–1)
 *   below which a window is considered silent (~ -40 dBFS).
 * @param {number} [options.windowMs=20] - RMS analysis window in milliseconds.
 * @returns {number} Trimmed duration in seconds. Falls back to the full
 *   duration if the decoded data isn't available.
 */
export function getTrimmedDuration(source, options = {}) {
  const { silenceThreshold = 0.01, windowMs = 20 } = options;

  const audioBuffer =
    typeof source?.getDecodedData === "function"
      ? source.getDecodedData()
      : source;

  if (!audioBuffer || typeof audioBuffer.getChannelData !== "function") {
    return source?.getDuration?.() ?? 0;
  }

  const { sampleRate, length, numberOfChannels } = audioBuffer;
  const windowSize = Math.max(1, Math.floor((windowMs / 1000) * sampleRate));

  const channels = [];
  for (let c = 0; c < numberOfChannels; c++) {
    channels.push(audioBuffer.getChannelData(c));
  }

  for (let end = length; end > 0; end -= windowSize) {
    const start = Math.max(0, end - windowSize);
    let peakRms = 0;

    for (let c = 0; c < channels.length; c++) {
      const data = channels[c];
      let sumSquares = 0;
      for (let i = start; i < end; i++) {
        sumSquares += data[i] * data[i];
      }
      const rms = Math.sqrt(sumSquares / (end - start));
      if (rms > peakRms) peakRms = rms;
    }

    if (peakRms > silenceThreshold) {
      return end / sampleRate;
    }
  }

  return 0;
}

/**
 * Default time display value in "mm:ss" format
 */
const DEFAULT_TIME = "00:00";

/**
 * WaveSurfer configuration constants
 */
const WAVE_CONFIG = {
  // WAVE_COLOR: "var(--wave-color)",
  // PROGRESS_COLOR: "var(--progress-color)",
  WAVE_COLOR: "#909090",
  PROGRESS_COLOR: "#0265dc",
  HEIGHT: 50,
  BAR_WIDTH: 2,
  BAR_GAP: 1,
  BAR_HEIGHT: 0.7,
  BAR_RADIUS: 3,
  CURSOR_WIDTH: 0,
  CHANNEL_HEIGHT: 50,
};

const audioPlayerStyle = style({
  display: "flex",
  flexDirection: "row",
  width: "full",
  position: "relative",
  alignItems: "center",
  gap: 12,
  minWidth: 300,
});

const timeCodeStyle = style({
  "--audio-player-timecode-width": { type: "width", value: 36 },
  position: "relative",
  font: "ui",
  color: "neutral-subdued",
  userSelect: "none",
  minWidth: "var(--audio-player-timecode-width)",
  textAlign: "center",
});

const waveformStyle = style({
  position: "relative",
  flexGrow: 1,
  opacity: "var(--waveform-opacity, 1)",
  height: "full",
  "--wave-color": {
    type: "color",
    value: "gray-600",
  },
  "--progress-color": {
    type: "color",
    value: "blue-600",
  },
  "--region-color": {
    type: "color",
    value: "gray-100",
  },
  "--region-color": {
    type: "color",
    value: "gray-100",
  },
  "--region-color-hover": {
    type: "color",
    value: "gray-300",
  },
  "--region-color-active": {
    type: "color",
    value: "gray-900",
  },
  "--region-color-invalid": {
    type: "color",
    value: "notice-700",
  },
  "--region-color-invalid-hover": {
    type: "color",
    value: "notice-900",
  },
  "--region-color-invalid-active": {
    type: "color",
    value: "notice-1000",
  },
});

const loadingContainerStyle = style({
  "--loading-container-height": {
    type: "height",
    value: 48,
  },
  width: "full",
  height: "var(--loading-container-height)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const loadingDotsStyle = style({
  "--loading-dots-height": {
    type: "height",
    value: 2,
  },
  width: "full",
  height: "var(--loading-dots-height)",
  background:
    "linear-gradient(to right, transparent, currentColor, transparent)",
  animation: "loading 1.5s infinite",
});

// const regionsPlugin = RegionsPlugin.create();
// const plugins = [regionsPlugin];

/**
 * AudioPlayer component for playing audio files.
 *
 * @param url - The URL of the audio file.
 * @param onDownload - Optional callback function for handling download events.
 */
export const AudioPlayer = ({
  ref,

  url,
  isAudioJobScheduled,
  onRegenerateAudio,
  needsRegeneration = false,
  isEditorEmpty = false,
  isFirstAudioGeneration = false,

  videoRef,

  segments = [],

  duration,

  onReady,
  onRegionPress,
  onRegionIn,
  onRegionOut,
  onPreviewPlay,
}) => {
  const id = useId();

  const [isLoaded, setIsLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(DEFAULT_TIME);
  // const { sendClickEvent, sendDragEvent } = useAnalytics();
  const audioRef = createRef();
  const waveformRef = useRef();
  const regionsRef = useRef();
  const segmentsRef = useRef([]);
  const durationRef = useRef(DEFAULT_TIME);
  const waveformContainerRef = useRef(null);
  const isUserDragging = useRef(false);
  // const localize = useLocalize();

  const colorScheme = useStore(store, (state) => state.colorScheme);
  const isDarkTheme = colorScheme === "dark";

  useEffect(() => {
    setIsPaused(true);
    setIsLoaded(false);
    setCurrentTime(DEFAULT_TIME);
  }, [url]);

  /**
   * Initializes the WaveSurfer instance and sets up event listeners for the audio player.
   *
   * @param url - The URL of the audio file.
   * @param isAudioJobScheduled - Whether an audio job is scheduled.
   */
  useEffect(() => {
    if (url && !isAudioJobScheduled) {
      initializeWaveform(url);
    }
    return () => {
      waveformRef.current?.destroy();
    };
  }, [url, isAudioJobScheduled]);

  const initializeWaveform = (audioUrl) => {
    if (!waveformContainerRef.current || !audioUrl) return;

    if (waveformRef.current) {
      waveformRef.current.destroy();
    }

    waveformRef.current = WaveSurfer.create({
      container: `#audioPlayer-${id}`,
      waveColor: WAVE_CONFIG.WAVE_COLOR,
      progressColor: WAVE_CONFIG.PROGRESS_COLOR,
      height: WAVE_CONFIG.HEIGHT,
      barWidth: WAVE_CONFIG.BAR_WIDTH,
      barGap: WAVE_CONFIG.BAR_GAP,
      normalize: true,
      barHeight: WAVE_CONFIG.BAR_HEIGHT,
      dragToSeek: true,
      barRadius: WAVE_CONFIG.BAR_RADIUS,
      cursorWidth: WAVE_CONFIG.CURSOR_WIDTH,
      splitChannels: [
        {
          height: WAVE_CONFIG.CHANNEL_HEIGHT,
          container: "#audio-result",
        },
        {
          height: 0,
          container: "#audio-result",
        },
      ],
      url: audioUrl,
    });

    if (typeof ref === "function") {
      ref(waveformRef.current);
    } else if (ref) {
      ref.current = waveformRef.current;
    }

    /**
     * Event listener for when the waveform is ready.
     * Sets the duration of the audio and marks it as loaded.
     */
    waveformRef.current.on("ready", () => {
      if (!waveformRef.current) return;

      durationRef.current = getFormattedTime(waveformRef.current.getDuration());

      if (videoRef?.current) {
        videoRef?.current?.pause?.();
        videoRef.current.currentTime = 0;
      }

      setIsLoaded(true);
      onReady?.(waveformRef.current);
    });
    /**
     * Event listener for when the audio finishes playing.
     * Marks the audio as paused.
     */
    waveformRef.current.on("finish", () => {
      setIsPaused(true);
      isUserDragging.current = false;
    });

    waveformRef.current.on("play", () => {
      setIsPaused(false);
      videoRef?.current?.play?.();
    });

    waveformRef.current.on("seeking", (currentTime) => {
      if (videoRef?.current) {
        videoRef.current.currentTime = currentTime;
      }
    });

    waveformRef.current.on("timeupdate", (currentTime) => {
      if (videoRef?.current) {
        if (Math.abs(videoRef.current.currentTime - currentTime) > 0.2) {
          videoRef.current.currentTime = currentTime;
        }
      }
    });

    waveformRef.current.on("pause", () => {
      setIsPaused(true);
      videoRef?.current?.pause?.();
    });

    waveformRef.current.on("finish", () => {
      setIsPaused(true);
      videoRef?.current?.pause?.();
    });

    waveformRef.current.on("drag", () => {
      isUserDragging.current = true;
    });

    waveformRef.current.on("click", () => {
      isUserDragging.current = true;
    });

    /**
     * Event listener for when the audio time updates.
     * Updates the current time display.
     */
    waveformRef.current.on("timeupdate", (currentTime) => {
      setCurrentTime(getFormattedTime(currentTime));
      // Only send drag event if user is dragging
      if (isUserDragging.current) {
        // sendDragEvent({
        //   subtype: AnalyticsEventSubtype.SLIDER,
        //   subcategory: AnalyticsSubcategory.CONTENT_MODULE,
        //   value: getFormattedTime(currentTime)
        // });
        isUserDragging.current = false;
      }
    });
  };

  useEffect(() => {
    if (waveformRef.current && !isAudioJobScheduled && segments?.length > 0) {
      const regions = RegionsPlugin.create();
      waveformRef.current.registerPlugin(regions);

      waveformRef.current.on("decode", () => {
        segments.forEach((segment, index) => {
          const segmentElement = document.createElement("div");
          segmentsRef.current[index] = segmentElement;

          const region = regions.addRegion({
            // id: `segment-${index}`,

            content: segmentElement,
            start: segment.start,
            end: segment.end,
            color: "transparent",
            drag: false,
            resize: false,
          });

          if (segment.isInvalid) {
            region.element.part.add("invalid");
          }
        });
      });

      regions.on("region-clicked", (region, e) => {
        if (segments.length > 1) {
          e.stopPropagation();
        }
        const index = regions.regions.indexOf(region);
        const segment = segments[index];
        onRegionPress?.({ segment, region, waveform: waveformRef.current });
      });

      regions.on("region-in", (region) => {
        const index = regions.regions.indexOf(region);
        const segment = segments[index];
        onRegionIn?.({ segment, region, waveform: waveformRef.current });
      });

      regions.on("region-out", (region) => {
        const index = regions.regions.indexOf(region);
        const segment = segments[index];
        onRegionOut?.({ segment, region, waveform: waveformRef.current });
      });
    }
  }, [segments, isAudioJobScheduled]);

  useEffect(() => {
    if (!isLoaded || !waveformContainerRef.current) return;

    let waveColor, progressColor;

    try {
      const lightDarkRegexp = /light-dark\(([^,]+),\s*([^)]+)\)/;

      const waveColorTheme = getComputedStyle(
        waveformContainerRef.current,
      ).getPropertyValue("--wave-color");
      const progressColorTheme = getComputedStyle(
        waveformContainerRef.current,
      ).getPropertyValue("--progress-color");

      waveColor = waveColorTheme.match(lightDarkRegexp)?.[isDarkTheme ? 2 : 1];
      progressColor =
        progressColorTheme.match(lightDarkRegexp)?.[isDarkTheme ? 2 : 1];
    } catch (error) {
      /* ignore */
    }

    const options = {
      waveColor: waveColor ?? WAVE_CONFIG.WAVE_COLOR,
      progressColor: progressColor ?? WAVE_CONFIG.PROGRESS_COLOR,
    };

    waveformRef.current?.setOptions(options);
  }, [isLoaded, isDarkTheme]);

  /**
   * Handler for the play/pause/regenerate button click event.
   */
  const buttonClickHandler = () => {
    if ((needsRegeneration || isFirstAudioGeneration) && onRegenerateAudio) {
      // sendClickEvent({
      //   subtype: AnalyticsEventSubtype.GENERATE_AUDIO,
      //   subcategory: AnalyticsSubcategory.SCRIPT_MODULE
      // });
      onRegenerateAudio();
      return;
    }
    if (!waveformRef.current || !isLoaded) return;

    if (isPaused) {
      onPreviewPlay?.();
    }

    waveformRef.current.playPause();

    // if (waveformRef.current.isPlaying()) {
    //   waveformRef.current.pause();
    //   setIsPaused(true);
    // } else {
    //   // sendClickEvent({
    //   //   subtype: AnalyticsEventSubtype.PLAY_AUDIO,
    //   //   subcategory: AnalyticsSubcategory.SCRIPT_MODULE
    //   // });
    //   waveformRef.current.play();
    //   setIsPaused(false);
    // }
  };

  /**
   * Determines the appropriate button label based on the current state of the audio player.
   *
   * @returns A localized string for the button label that reflects the current state:
   *   - "Preview Audio" for first generation or when paused
   *   - "Generating" when an audio job is scheduled or loading
   *   - "Regenerate" when audio needs regeneration
   *   - "Pause" when audio is playing
   */
  const buttonLabel = () => {
    if (isFirstAudioGeneration || isEditorEmpty) return "Preview Audio";
    if (isAudioJobScheduled || (!isLoaded && !needsRegeneration))
      return "Generating";
    if (needsRegeneration) return "Regenerate";
    return isPaused ? "Preview Audio" : "Pause";
  };

  return (
    <>
      <div className={audioPlayerStyle}>
        <audio ref={audioRef} src={url} />

        <TooltipTrigger delay={0}>
          <ActionButton
            onPress={buttonClickHandler}
            // className={[
            //   styles["play-button"],
            //   needsRegeneration && !isEditorEmpty && styles["regenerate-button"],
            //   needsRegeneration && !isEditorEmpty && isDarkTheme && styles["regenerate-button-dark"]
            // ]}
            isQuiet
            isDisabled={
              isAudioJobScheduled ||
              isEditorEmpty ||
              (!isLoaded && !needsRegeneration && !isFirstAudioGeneration)
            }
            aria-label={buttonLabel()}
          >
            {isAudioJobScheduled || (!isLoaded && !isEditorEmpty) ? (
              <ProgressCircle
                size="S"
                isIndeterminate
                staticColor="auto"
                aria-label="loading"
                styles={style({ gridArea: "icon" })}
              />
            ) : isFirstAudioGeneration || isEditorEmpty ? (
              <PlayIcon />
            ) : needsRegeneration ? (
              <RefreshIcon />
            ) : isPaused ? (
              <PlayIcon />
            ) : (
              <PauseIcon />
            )}
          </ActionButton>
          <Tooltip>
            <Text>{buttonLabel()}</Text>
          </Tooltip>
        </TooltipTrigger>
        <div className={timeCodeStyle} role="button" aria-label={currentTime}>
          {currentTime}
        </div>
        <div
          className={"waveform" + waveformStyle}
          style={{
            display:
              url && isLoaded && !isAudioJobScheduled && !needsRegeneration
                ? "block"
                : "none",
          }}
          id={`audioPlayer-${id}`}
          ref={waveformContainerRef}
          role="region"
          tabIndex={0}
        ></div>

        {(!isLoaded || isAudioJobScheduled || needsRegeneration) && (
          <div className={loadingContainerStyle} role="region">
            <div className={loadingDotsStyle} aria-hidden="true"></div>
          </div>
        )}

        <div
          className={timeCodeStyle}
          role="button"
          aria-label={
            isLoaded && !isAudioJobScheduled
              ? durationRef.current
              : DEFAULT_TIME
          }
        >
          {isLoaded && !isAudioJobScheduled
            ? durationRef.current
            : DEFAULT_TIME}
        </div>
      </div>
      {/* {isLoaded &&
        segments?.map(
          (segment, index) =>
            !!segmentsRef.current[index] &&
            createPortal(
              <div style={{}} />,
              segmentsRef.current[index],
              `segment-${index}-${+new Date()}`,
            ),
        )} */}
    </>
  );
};
